/** Interactive Gantt chart showing measure timing across the year.

Renders a grid of measures × months. Bar cells are coloured by species
(bachmuschel = gold, helm-azurjungfer = blue), or by the measure's own
colour for standalone measures. A legend below the chart explains the
species colours.

Clicking a bar expands a detail panel with the full description, image,
and details.

Data-driven: receives measures from config + catalogue entries.
No hardcoded measure data.
 */

import { memo, useMemo, useState } from "react";

import { getMonthLabels } from "../constants/months";
import { useLanguageContext } from "../hooks/useLanguage";
import { renderEmphasis } from "../utils/emphasis";
import type { CatalogueEntry, MeasureConfig, RegionConfig } from "../types";

interface GanttChartProps {
  config: RegionConfig;
  catalogue: CatalogueEntry[];
}

function formatMonthsLabel(months: number[], labels: string[], allYearLabel: string): string {
  if (months.length === 0) return "—";
  if (months.length === 12) return allYearLabel;
  const sorted = [...months].sort((a, b) => a - b);
  return `${labels[sorted[0] - 1]}–${labels[sorted[sorted.length - 1] - 1]}`;
}

export const GanttChart = memo(function GanttChart({ config, catalogue }: GanttChartProps) {
  const { t, lang } = useLanguageContext();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [imgError, setImgError] = useState(false);
  const monthLabels = getMonthLabels(lang);

  const catalogueById = useMemo(
    () => new Map(catalogue.map((entry) => [entry.measure_id, entry])),
    [catalogue],
  );

  const speciesNameById = useMemo(
    () => new Map(config.species.map((s) => [s.id, s.name])),
    [config.species],
  );

  const speciesColorById = useMemo(
    () => new Map(config.species.map((s) => [s.id, s.color])),
    [config.species],
  );

  /** Get the localised label for a measure, falling back to the config label. */
  const measureLabel = (measureId: string, fallback: string): string => {
    const key = `measure_${measureId}`;
    const val = t(key);
    return val === key ? fallback : val;
  };

  /** Get the localised species name. */
  const speciesName = (speciesId: string, fallback: string): string => {
    const key = `species_${speciesId}`;
    const val = t(key);
    return val === key ? fallback : val;
  };

  /** Pick the localised text field based on current language. */
  const localisedText = (de: string, en: string): string => (lang === "en" ? en || de : de);

  const selectedMeasure = useMemo(
    () => config.measures.find((m) => m.id === selectedId) || null,
    [config.measures, selectedId],
  );

  const selectedEntry = selectedId ? catalogueById.get(selectedId) : undefined;
  const selectedSpeciesLabel = selectedMeasure?.species
    ? speciesName(
        selectedMeasure.species,
        speciesNameById.get(selectedMeasure.species) || selectedMeasure.species,
      )
    : t("standalone_measure");

  return (
    <div className="space-y-4">
      {/* Compact stacked rows for phones: same data, no fixed label column. */}
      <div className="space-y-2 sm:hidden">
        <div className="grid grid-cols-12 gap-px px-1">
          {monthLabels.map((label) => (
            <div
              key={label}
              className="text-center font-utility text-[0.52rem] font-medium text-flima-slate/50"
            >
              {label}
            </div>
          ))}
        </div>
        {config.measures.map((measure: MeasureConfig) => {
          const isSelected = selectedId === measure.id;
          const measureSpeciesColor = speciesColorById.get(measure.species) || measure.color;
          return (
            <button
              key={measure.id}
              type="button"
              onClick={() => setSelectedId(isSelected ? null : measure.id)}
              aria-pressed={isSelected}
              className={`block w-full rounded-lg p-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river ${
                isSelected ? "bg-flima-river/10" : "hover:bg-flima-mist/60"
              }`}
            >
              <span className="mb-1.5 block break-words text-xs font-semibold leading-tight text-flima-slate">
                {measureLabel(measure.id, measure.label)}
              </span>
              <span className="grid grid-cols-12 gap-px">
                {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => {
                  const isActive = measure.months.includes(month);
                  const bg = isActive ? measureSpeciesColor : null;
                  return (
                    <span
                      key={month}
                      className={`h-5 rounded-sm ${
                        isActive ? "" : "bg-flima-spruce/5"
                      }`}
                      style={isActive ? { backgroundColor: bg || measureSpeciesColor } : undefined}
                    >
                      {isActive ? (
                        <span className="sr-only">
                          {monthLabels[month - 1]}
                        </span>
                      ) : null}
                    </span>
                  );
                })}
              </span>
            </button>
          );
        })}
      </div>

      {/* Month header */}
      <div className="hidden gap-2 sm:flex">
        <div className="w-44 shrink-0 sm:w-52" />
        <div className="grid flex-1 grid-cols-12 gap-px">
          {monthLabels.map((label) => (
            <div
              key={label}
              className="text-center font-utility text-[0.6rem] font-medium text-flima-slate/50"
            >
              {label}
            </div>
          ))}
        </div>
      </div>

      {/* Measure rows */}
      <div className="hidden space-y-1 sm:block">
        {config.measures.map((measure: MeasureConfig) => {
          const isSelected = selectedId === measure.id;
          const measureSpeciesColor = speciesColorById.get(measure.species) || measure.color;
          return (
            <button
              key={measure.id}
              type="button"
              onClick={() => setSelectedId(isSelected ? null : measure.id)}
              aria-pressed={isSelected}
              className={`flex w-full items-center gap-2 rounded-md py-1.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river ${
                isSelected ? "bg-flima-river/10" : "hover:bg-flima-mist/60"
              }`}
            >
              {/* Label */}
              <div className="w-44 shrink-0 truncate text-left text-xs font-medium text-flima-slate sm:w-52">
                {measureLabel(measure.id, measure.label)}
              </div>
              {/* Bar grid — coloured by species, or by measure colour for standalone measures */}
              <div className="relative flex-1">
                <div className="grid grid-cols-12 gap-px">
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => {
                    const isActive = measure.months.includes(month);
                    // Each bar cell is coloured by the measure's own species,
                    // or the measure colour when it is standalone.
                    const bg = isActive ? measureSpeciesColor : null;
                    return (
                      <div
                        key={month}
                        className={`h-5 rounded-sm ${
                          isActive ? "" : "bg-flima-spruce/5"
                        }`}
                        style={isActive ? { backgroundColor: bg || measureSpeciesColor } : undefined}
                      >
                        {isActive ? (
                          <span className="sr-only">
                            {monthLabels[month - 1]}
                          </span>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 border-t border-flima-spruce/10 pt-3">
        {config.species.map((species) => (
          <div key={species.id} className="flex items-center gap-2">
            <span
              className="h-3 w-5 shrink-0 rounded-sm"
              style={{ backgroundColor: species.color }}
              aria-hidden="true"
            />
            <span className="text-xs font-medium text-flima-slate">
              {speciesName(species.id, species.name)}
            </span>
          </div>
        ))}
      </div>

      {/* Detail panel */}
      {selectedMeasure ? (
        <div className="rounded-lg border border-flima-spruce/15 bg-flima-mist/40 p-3 sm:p-4">
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <h4 className="break-words font-display text-sm font-semibold text-flima-spruce">
                {measureLabel(selectedMeasure.id, selectedMeasure.label)}
              </h4>
              <p className="mt-0.5 break-words text-xs text-flima-slate/55">
                {t("species_label")}: {selectedSpeciesLabel}
                {" · "}
                {t("time_range")}: {formatMonthsLabel(selectedMeasure.months, monthLabels, t("all_year"))}
              </p>

              {selectedEntry?.image_path && !imgError ? (
                <img
                  src={selectedEntry.image_path}
                  alt={measureLabel(selectedMeasure.id, selectedMeasure.label)}
                  loading="lazy"
                  onError={() => setImgError(true)}
                  className="mt-3 h-36 w-full max-w-full rounded-md bg-flima-mist/40 object-contain sm:max-w-sm"
                />
              ) : null}

              {selectedEntry ? (
                <p className="mt-3 break-words text-sm leading-relaxed text-flima-slate/80">
                  {renderEmphasis(
                    localisedText(selectedEntry.description, selectedEntry.description_en) || t("no_data"),
                  )}
                </p>
              ) : (
                <p className="mt-3 text-sm text-flima-slate/50">{t("no_data")}</p>
              )}

              {selectedEntry?.details ? (
                <div className="mt-3 rounded-md bg-white/70 p-3">
                  <p className="break-words text-xs leading-relaxed text-flima-slate/70">
                    {renderEmphasis(localisedText(selectedEntry.details, selectedEntry.details_en))}
                  </p>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
});
